import { EntityItem } from '@features/entity-picker'
import {
  useApprovalDomainApprover,
  useUpdateApprovalDomainApprover,
} from '@hooks/useApprovalDomainApprovers'
import { useSet } from '@hooks/useSet'
import { Modal } from 'components/notifications'
import { useState } from 'react'
import { ApproverModalContent } from './ApproverModalContent'
import * as Sentry from '@sentry/react'

interface EditApproverModalProps {
  approvalDomainId: string
  approverId: number
  onClose: () => void
  onError: () => void
  onSuccess: () => void
}

export const EditApproverModal = ({
  approvalDomainId,
  approverId,
  onClose,
  onError,
  onSuccess,
}: EditApproverModalProps) => {
  const approver = useApprovalDomainApprover(approvalDomainId, approverId)

  const {
    set: selectedFacilityIds,
    add: selectFacility,
    remove: unselectFacility,
    clear: clearSelectedFacilityIds,
  } = useSet(new Set(approver?.facilityIds ?? []))

  const {
    set: selectedDepartmentIds,
    add: selectDepartment,
    remove: unselectDepartment,
    clear: clearSelectedDepartmentIds,
  } = useSet(new Set(approver?.workdayDepartmentIds ?? []))

  const [allFacilities, setAllFacilities] = useState(
    approver?.allFacilities ?? false,
  )
  const [allDepartments, setAllDepartments] = useState(
    approver?.allWorkdayDepartments ?? false,
  )

  const editApproverMutation = useUpdateApprovalDomainApprover()

  const save = async () => {
    try {
      await editApproverMutation.mutateAsync({
        allFacilities,
        allWorkdayDepartments: allDepartments,
        approvalDomainGlobalId: approvalDomainId,
        approverId,
        facilityIds: allFacilities ? [] : Array.from(selectedFacilityIds),
        workdayDepartmentIds: allDepartments
          ? []
          : Array.from(selectedDepartmentIds),
      })

      onClose()
      onSuccess()
    } catch (error) {
      Sentry.captureException(error, {
        tags: {
          approvalDomainId,
          approverId,
        },
      })
      onError()
    }
  }

  const isValid =
    (allFacilities || selectedFacilityIds.size > 0) &&
    (allDepartments || selectedDepartmentIds.size > 0)

  return (
    <Modal
      closeOnBackgroundClick={false}
      hidden={false}
      onClose={onClose}
      title="Edit Approver"
      variant="custom"
      content={
        <ApproverModalContent
          disableSaveButton={!isValid}
          userSection={
            approver?.user && (
              <EntityItem
                className="bg-neutral-100"
                primaryText={approver.user.name}
                secondaryText={approver.user.email}
              />
            )
          }
          selectedFacilityIds={selectedFacilityIds}
          selectedDepartmentIds={selectedDepartmentIds}
          allFacilities={allFacilities}
          allDepartments={allDepartments}
          onFacilitySelect={(facility, selected) => {
            const op = selected ? selectFacility : unselectFacility
            op(facility.id)
          }}
          onDepartmentSelect={(department, selected) => {
            const op = selected ? selectDepartment : unselectDepartment
            op(department.id)
          }}
          onAllFacilitiesToggle={(selected) => {
            if (selected) clearSelectedFacilityIds()
            setAllFacilities(selected)
          }}
          onAllDepartmentsToggle={(selected) => {
            if (selected) clearSelectedDepartmentIds()
            setAllDepartments(selected)
          }}
          onCancel={onClose}
          onSave={() => void save()}
          isLoading={editApproverMutation.isLoading}
          saveButtonText="Save"
        />
      }
    />
  )
}
